import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from 'src/shared/lib/useQuery';
import { useNavigate } from '@tanstack/react-router';
import { getApiUrl } from 'src/shared/lib/getApiUrl';
import { RedirectException } from 'src/shared/queries/errors/RedirectException';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Model } from 'src/shared/types/model.types';
import { Me } from 'src/shared/types/user.types';
import { QueryResult } from 'src/shared/types/util.types';

export class FetchMeException extends Error {
  public statusCode: number;
  public data: any;

  constructor(message: string, statusCode: number, data?: any) {
    super(message);
    this.data = data;
    this.statusCode = statusCode;
    this.name = 'FetchMeException';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function useMe(): QueryResult<Me>;
export function useMe(redirectPrefix?: string, disabled?: boolean): QueryResult<Me>;
export function useMe<const Key extends keyof Me>(prop?: Key): QueryResult<Me[Key]>;
export function useMe<const Key extends keyof Me>(
  redirectPrefixOrProp?: Key | string,
  disabled?: boolean
): QueryResult<Me | Me[Key]> {
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const redirectPrefix = typeof redirectPrefixOrProp === 'string' ? redirectPrefixOrProp : undefined;
  const prop = typeof redirectPrefixOrProp === 'string' ? undefined : redirectPrefixOrProp;

  const select = useCallback(
    (data: Me): Me | Me[Key] => {
      return prop ? data?.[prop] : data;
    },
    [prop]
  );

  return useQuery({
    queryKey: ['user'],
    queryFn: getData,
    enabled: isAuthenticated && !disabled,
    staleTime: 0,
    retry: 3,
    select,
  });

  async function getData(): Promise<Me> {
    try {
      const token = await getAccessTokenSilently();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer: ${token}`,
      };
      const url = new URL(getApiUrl() + '/api/v2/account');
      const res = await fetch(`${url.toString()}`, {
        headers: headers,
      });

      const data = await res.json();

      if (res.status === 307) {
        const toRedirect = data.redirect;
        delete data.redirect;
        navigate({ to: `${redirectPrefix ?? ''}${toRedirect}` });
        throw new RedirectException();
      } else if (!res.ok) {
        throw new FetchMeException('Something went wrong fetching /account', res.status, data);
      }

      // default topic bookmark if no bookmarks are set
      return {
        ...data,
        settings: {
          ...(data.settings || {}),
          bookmarks: data.settings?.bookmarks || ['topic', 'topics'],
        },
      } as Me;
    } catch (error) {
      if (!(error instanceof Error)) {
        throw new Error('Unknown error occurred');
      }
      mixpanel.track(`Failed Fetch Me`, error);
      console.error('Failed to fetch user');
      console.error(error);
      console.error({ message: error.message, stack: error.stack });
      throw error;
    }
  }
}

type UpdateUserProps = {
  name?: string;
  settings?: Me['settings'];
};

export function useMutateUser() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentOrg } = useOrg();
  const { apiFetch } = useAuth();

  const updateUser = useMutation({
    mutationFn: async ({ name, settings }: UpdateUserProps) => {
      const payload: UpdateUserProps = {};
      if (name) {
        payload.name = name;
      }
      if (settings) {
        payload.settings = settings;
      }

      const res = await apiFetch('/api/v2/account', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(JSON.stringify(data));
      }
      return data;
    },
    onSuccess: async (_data, variables) => {
      await queryClient.refetchQueries({ queryKey: ['user'] });

      // update bookmarks if there are any
      if (variables.settings?.bookmarks) {
        const models = queryClient.getQueryData<Model[]>(['models', genQueryKey(currentOrg?.id)]);
        if (!models) {
          return;
        }
        queryClient.setQueryData(
          ['models', genQueryKey(currentOrg?.id)],
          models.map((model) => ({
            ...model,
            bookmarked: (variables.settings?.bookmarks || []).includes(model.id),
          }))
        );
      }
    },
    onError: async (error) => {
      console.log('Settings - account: failed to update account: ' + error);
      toast({
        title: 'Failed to update account',
        description: 'Please try again',
        variant: 'warning',
        id: 'failed-account-update',
      });
    },
  });
  const resendVerificationEmail = useMutation({
    mutationFn: async () => {
      const res = await apiFetch('/api/v2/account/emailverification', {
        method: 'POST',
      });

      if (!res.ok) {
        toast({
          title: 'Failed to resend verification email',
          description: 'Please try again',
          variant: 'warning',
          id: 'failed-resend-verification',
        });
      }
      return undefined;
    },
    onSuccess: () => {
      toast({
        title: 'Verification email sent!',
        description: 'Please check your inbox.',
        id: 'verification-email-sent',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'warning',
        title: 'Something went wrong',
        description: error.message || 'Please try again',
        id: 'failed-resend-verification',
      });
    },
  });

  return { updateUser, resendVerificationEmail };
}
